/**
 * Batch #14.309 — Pages légales publiques : /conditions-utilisation et /politique-confidentialite.
 * Texte servi par GET /api/public/legal/{slug} (défaut ou personnalisé dans Paramètres › Pages légales).
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Printer, Loader2 } from "lucide-react";
import { API } from "@/api";

const OTHER = { "conditions-utilisation": ["politique-confidentialite", "Politique de confidentialité"], "politique-confidentialite": ["conditions-utilisation", "Conditions d'utilisation"] };

export function parseLegal(body) {
  const blocks = [];
  let para = [];
  const flush = () => { if (para.length) { blocks.push({ t: "p", text: para.join(" ") }); para = []; } };
  for (const raw of String(body || "").split("\n")) {
    const line = raw.trim();
    if (!line) { flush(); continue; }
    if (line.startsWith("# ")) { flush(); blocks.push({ t: "h", text: line.slice(2), id: `s${blocks.length}` }); continue; }
    if (line.startsWith("- ")) {
      flush();
      const last = blocks[blocks.length - 1];
      if (last?.t === "ul") last.items.push(line.slice(2)); else blocks.push({ t: "ul", items: [line.slice(2)] });
      continue;
    }
    para.push(line);
  }
  flush();
  return blocks;
}

const inline = (text) => text.split(/(\*\*[^*]+\*\*)/g).map((part, i) => (part.startsWith("**") ? <strong key={i} className="font-semibold text-slate-900">{part.slice(2, -2)}</strong> : part));

export function LegalText({ body, className = "" }) {
  const blocks = useMemo(() => parseLegal(body), [body]);
  return (
    <div className={`space-y-3 text-[15px] leading-relaxed text-slate-700 ${className}`} data-testid="legal-text">
      {blocks.map((b, i) => {
        if (b.t === "h") return <h2 key={i} id={b.id} className="pt-5 text-base md:text-lg font-bold text-[#072354] scroll-mt-24">{b.text}</h2>;
        if (b.t === "ul") return <ul key={i} className="list-disc pl-6 space-y-1.5">{b.items.map((it, j) => <li key={j}>{inline(it)}</li>)}</ul>;
        return <p key={i} className="whitespace-pre-line">{inline(b.text)}</p>;
      })}
    </div>
  );
}

export default function LegalPage({ slug }) {
  const [page, setPage] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    setPage(null); setError("");
    axios.get(`${API}/public/legal/${slug}`).then((r) => setPage(r.data)).catch(() => setError("Page introuvable."));
    window.scrollTo(0, 0);
  }, [slug]);
  const toc = useMemo(() => parseLegal(page?.body).filter((b) => b.t === "h"), [page]);
  const [otherSlug, otherLabel] = OTHER[slug];

  return (
    <div className="min-h-screen bg-[#f4f6fb] text-slate-800" data-testid={`legal-page-${slug}`}>
      <header className="bg-[#072354] text-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8 sm:py-12">
          <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-blue-200/70 hover:text-white transition-colors" data-testid="legal-back-home"><ArrowLeft className="w-3.5 h-3.5" /> DG Comptabilité</Link>
          <h1 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight" data-testid="legal-title">{page?.title || (slug === "conditions-utilisation" ? "Conditions d'utilisation" : "Politique de confidentialité")}</h1>
          {page && <p className="mt-2 text-sm text-blue-200/70" data-testid="legal-updated">{page.company.company} · Dernière mise à jour : {page.updated_at}</p>}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-8 sm:py-10 grid lg:grid-cols-[220px_1fr] gap-8 print:block">
        <aside className="hidden lg:block print:hidden">
          <nav className="sticky top-6 space-y-1 text-xs" data-testid="legal-toc">
            <p className="uppercase tracking-wider text-[10px] font-semibold text-slate-400 mb-2">Sommaire</p>
            {toc.map((h) => <a key={h.id} href={`#${h.id}`} className="block text-slate-500 hover:text-[#072354] truncate transition-colors">{h.text}</a>)}
            <div className="pt-4 mt-4 border-t border-slate-200 space-y-1">
              <Link to={`/${otherSlug}`} className="block text-[#072354] font-medium hover:underline" data-testid="legal-other-link">{otherLabel} →</Link>
              <button type="button" onClick={() => window.print()} className="inline-flex items-center gap-1 text-slate-500 hover:text-[#072354]" data-testid="legal-print-btn"><Printer className="w-3 h-3" /> Imprimer</button>
            </div>
          </nav>
        </aside>

        <article className="bg-white rounded-2xl border border-slate-200 shadow-sm px-6 sm:px-10 py-8 print:border-0 print:shadow-none">
          {error && <p className="text-sm text-rose-700" data-testid="legal-error">{error}</p>}
          {!page && !error && <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>}
          {page && <LegalText body={page.body} />}
          {page && (
            <footer className="mt-10 pt-5 border-t border-slate-100 text-xs text-slate-400 flex flex-wrap gap-x-4 gap-y-1 print:hidden">
              <Link to={`/${otherSlug}`} className="hover:text-[#072354]">{otherLabel}</Link>
              <span>© {new Date().getFullYear()} {page.company.company}</span>
            </footer>
          )}
        </article>
      </main>
    </div>
  );
}
